import createScriptTag from './createScriptTag';
import {
  isOkToLoadScript,
  registerConsentChangeHandler,
  scriptWasDenied,
} from './privacyCompliance';
import fetchScriptData from './fetchScriptData';
import { logWarning, logError } from './log';
import {
  isValidString,
  isValidObject,
  isValidObjectButNotArray,
  objectValuesSortedByKey,
} from './utils';
import { memoizedRetryWithTimeout } from './convenience';

// User Consent Management (UCM) is responsible for displaying UI related to privacy laws including
// the General Data Protection Regulation (GDPR). Script Supplier will load the UCM script during
// initalization, before loading or populating any tags configured by the user.
const UCMScript = {
  name: 'user-consent-management',
  version: '^1.0.0',
  async: true,
};

/**
 * Notice this URL could reference the E1, E2 or E3 instances of One Data. The reason it is
 * hard-coded despite that fact is so the maintainers of Script Supplier can have legitimate
 * E1, E2 & E3 environments for development work. End-users & other developers will only ever
 * interact with the E3 script registry (on the E3 instance of One Data). So, a maintainer of this
 * package would set the URL below to the appropriate environment depending on
 * whether they are actively developing Script Supplier, making a build to distribute to
 * quality assurance (or other internal stakeholders) or making a production build to distribute
 * abroad & eventually reach end-users.
 */
const SCRIPT_REGISTRY_URL = 'https://functions.americanexpress.com/ReadScriptRegistry.v1';
const UCM_SCRIPT_REGEX = /^https:\/\/(cdaas-dev\.americanexpress\.com|q?www\.aexp-static\.com)\/one\/user-consent-management\/\d+\.\d+\.\d+-?\d*\/ucm\.js(\?|$)/i;
const EXACT_VERSION_CHECK_REGEX = /^\d+\.\d+\.\d+$/;

const scriptPreviouslyLoaded = (existingScripts, src) => {
  if (!UCM_SCRIPT_REGEX.test(src?.trim())) {
    return [...existingScripts].some((script) => script.src?.trim() === src?.trim());
  }
  return [...existingScripts].some((script) => UCM_SCRIPT_REGEX.test(script.src?.trim()));
};

const addToScriptConfig = (name, config) => {
  if (isValidObject(config) && !Array.isArray(config)) {
    if (!isValidObject(window.scriptConfig)) {
      window.scriptConfig = {};
    }

    window.scriptConfig[name] = config;
  }
};

const getExistingScripts = () => {
  return document.querySelectorAll('script');
};

const loadScriptOnPage = ({
  existingScripts,
  scriptAttributes,
  scriptObject,
}) => {
  const previouslyLoaded = scriptPreviouslyLoaded(existingScripts, scriptAttributes.src);
  if (!previouslyLoaded) {
    const { name, config } = scriptObject;
    addToScriptConfig(name, config);
    const scriptTag = createScriptTag(scriptAttributes);

    document.head.appendChild(scriptTag);
  }
};

const validateScript = (script) => {
  if (!isValidString(script?.name)) {
    throw new Error('No name was provided.');
  }

  if (!isValidString(script?.version)) {
    throw new Error('No version was provided.');
  }

  if (EXACT_VERSION_CHECK_REGEX.test(script.version.trim())) {
    logWarning(`An exact version is configured for ${script.name}. Because of this, critical security patches 
    and bug fixes won't be applied. It's unlikely this is intentional. It's recommended to use a range like "^1.0.0".`);
  }

  if (script.version.trim() === '*') {
    logWarning(`A star version range is used for ${script.name}. This is not recommended, as breaking changes will
    automatically be applied, increasing the risk of a production issue. It's recommended to use a range like "^1.0.0" instead.`);
  }
};

/** Returns a Promise */
const processScript = (args) => {
  let scriptNameForLog;
  let scriptIndexForLog;

  const handleError = (error, scriptName, scriptIndex) => {
    logWarning(`Experienced an error when loading script "${scriptName}" at index ${scriptIndex}.`, error);
  };

  try {
    const {
      script,
      index,
      scriptRegistryUrl,
      scriptEnvironment,
      pageLocale,
    } = args;

    scriptNameForLog = script?.name;
    scriptIndexForLog = index;
    const scriptNameCopy = scriptNameForLog; // Probably unnecessary, but unsure.
    const scriptIndexCopy = scriptIndexForLog; // Probably unnecessary, but unsure.
    validateScript(script);

    const fetchScriptDataInputs = {
      url: scriptRegistryUrl.trim(),
      environment: scriptEnvironment.trim(),
      name: script.name.trim(),
      version: script.version.trim(),
    };

    const fetchScriptDataAction = () => fetchScriptData({
      fetchFn: fetch,
      ...fetchScriptDataInputs,
    });

    return memoizedRetryWithTimeout(
      'scriptDataCache',
      objectValuesSortedByKey(fetchScriptDataInputs),
      fetchScriptDataAction
    ).then((scriptData) => {
      const scriptTagAttributes = {
        ...scriptData.attributes,
        async: script.async ? true : undefined,
        onload: script.onload ? script.onload : undefined,
        charset: 'UTF-8',
      };

      if (!isOkToLoadScript(script.name, scriptData.classifications, pageLocale)) {
        const scriptObjectCopy = { ...script };
        scriptWasDenied({
          name: script.name,
          version: scriptData.version,
          classifications: scriptData.classifications,
          pageLocale,
          loadOnPageFn: () => {
            loadScriptOnPage({
              existingScripts: getExistingScripts(),
              scriptAttributes: scriptTagAttributes,
              scriptObject: scriptObjectCopy,
            });
          },
        });
        return;
      }

      loadScriptOnPage({
        existingScripts: getExistingScripts(),
        scriptAttributes: scriptTagAttributes,
        scriptObject: script,
      });
    }).catch((reason) => {
      handleError(reason, scriptNameCopy, scriptIndexCopy);
    });
  } catch (error) {
    handleError(error, scriptNameForLog, scriptIndexForLog);
  }
  return Promise.resolve();
};

const validateArgsAndApplyDefaults = (args) => {
  if (!isValidObjectButNotArray(args) && args !== undefined) {
    logWarning('Expected arguments to `initialize` to be passed as an object.');
  }

  const scripts = args?.scripts || window?.scriptSupplierPreset || [];
  if (!Array.isArray(scripts)
      || scripts.some((item) => !isValidObject(item))) {
    throw new Error('Invalid script supplier config. Expected an array of objects.');
  }

  const scriptEnvironment = args?.scriptEnvironment || process.env.SCRIPT_SUPPLIER_SCRIPT_ENV;

  if (!isValidString(scriptEnvironment)) {
    throw new Error('Invalid/non-existent script environment. Expected a string.');
  }

  const pageLocale = args?.pageLocale || window?.scriptSupplierPageLocale;

  if (!isValidString(pageLocale)) {
    throw new Error('Invalid/non-existent page locale. Expected a string.');
  }

  return {
    scripts,
    scriptEnvironment,
    pageLocale,
  };
};

const initialize = (argsBeforeDefaults) => {
  try {
    const {
      scripts,
      scriptEnvironment,
      pageLocale,
    } = validateArgsAndApplyDefaults(argsBeforeDefaults);

    registerConsentChangeHandler();

    window.UCMPageLocale = pageLocale;

    const scriptsWithUCM = [UCMScript, ...scripts];

    return scriptsWithUCM.map((script, index) => processScript({
      script,
      index,
      scriptRegistryUrl: SCRIPT_REGISTRY_URL,
      scriptEnvironment,
      pageLocale,
    }));
  } catch (error) {
    logError('Experienced an issue and was not able to load the requested scripts.', error);
  }
  return [];
};

export default initialize;

export const unitTestExports = {
  SCRIPT_REGISTRY_URL,
  UCM_SCRIPT_REGEX,
  EXACT_VERSION_CHECK_REGEX,
  scriptPreviouslyLoaded,
  addToScriptConfig,
  getExistingScripts,
  loadScriptOnPage,
  validateScript,
  processScript,
  validateArgsAndApplyDefaults,
};
